import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import PageLayout from './PageLayout';
import ProbeterminButton from './ProbeterminButton';
import Popup from './Popup';
import Images from '../assets/Images';
import theme, { mobileBreakpoint } from '../theme';
import texts from '../locales/texts';
import { useAuth } from '../AuthContext';
import { StarBadgeWithIcon } from './shared/CommonElements';
import config from '../config';

// Keyframes for sparkles
const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4), 0 0 20px rgba(241, 222, 124, 0.3); }
  50% { box-shadow: 0 6px 30px rgba(219, 39, 98, 0.6), 0 0 30px rgba(241, 222, 124, 0.5); }
`;


const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  
`;

// Hero Section - Über Mich
const HeroSection = styled.section`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 60px;
  margin-bottom: 80px;
  
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    gap: 40px;
    margin-bottom: 60px;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  max-width: 600px;
`;

const HeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
`;

const StarIcon = styled.img`
  width: 70px;
  height: 70px;
  flex-shrink: 0;
  margin-right: 16px;

  @media (max-width: ${mobileBreakpoint}px) {
    width: 60px;
    height: 60px;
  }
`;

const MainTitle = styled.h1`
  font-size: 64px;
  margin: 0;
  line-height: 1;
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  font-style: italic;
  margin: 0 0 32px 0;
  color: ${props => props.theme.colors.secondary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }

`;

const Description = styled.div`
  font-size: 18px;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  
  p {
    margin-bottom: 16px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
`;

// Image Section
const ImageWithBlobWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 950px) {
    width: 320px;
    height: 320px;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 300px;
    height: 300px;
    margin: 0 auto;
  }
`;

const OwnerImageContainer = styled.div`
  position: relative;
  width: 400px;
  height: 400px;
  border-radius: 50% 20% 40% 10%;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border: 6px solid white;
  z-index: 2;
  
  @media (max-width: 950px) {
    width: 320px;
    height: 320px;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 280px;
    height: 280px;
    margin-top: 40px;
    position: relative;
  }
`;

const OwnerPicture = styled.picture`
  width: 100%;
  height: 100%;
  display: block;
`;

const OwnerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const BackgroundShape = styled.div`
  position: absolute;
  width: 500px;
  height: 500px;
  background-color: ${props => props.theme.colors.accent2};
  opacity: 0.3;
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 0;
  
  @media (max-width: 950px) {
    width: 400px;
    height: 400px;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 380px;
    height: 380px;
  }
`;

// Content Sections
const ContentSection = styled.section`
  margin: 80px 0;
  
  @media (max-width: ${mobileBreakpoint}px) {
    margin: 60px 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  
  @media (max-width: ${mobileBreakpoint}px) {
    justify-content: center;
    flex-wrap: wrap;
    padding: 0 10px;
  }
`;

const SectionHeaderStar = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  
  @media (max-width: ${mobileBreakpoint}px) {
    margin-left: 0;
    width: 50px;
    height: 50px;
    display: none;
  }
`;

const SectionHeaderStarSecond = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  margin-left: -76px;
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 90px;
    height: 90px;
    margin-left: unset;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: bold;
  margin: 0;
  color: ${props => props.titleColor || props.theme.colors.secondary};
  display: contents;
  
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 28px;
    text-align: center;
    display: block;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 24px 0;
  
  @media (max-width: ${mobileBreakpoint}px) {
    text-align: center;
  }
`;

const SectionContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  
  @media (max-width: ${mobileBreakpoint}px) {
    padding: 0 10px;
  }
`;

const SectionText = styled.div`
  font-size: 18px;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  margin-right: 76px;
  
  p {
    margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
  }
  
  strong {
    font-weight: 600;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    margin-right: unset;
  }
`;

// Call to Action Section
const CTASection = styled.section`
  background-color: ${props => props.theme.colors.accent2};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: 60px 40px;
  margin: 80px 0;
  
  @media (max-width: ${mobileBreakpoint}px) {
    padding: 40px 20px;
    margin: 60px 0;
  border-radius: ${props => props.theme.borderRadius.medium};
  }
`;

const CTAContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    gap: 24px;
    text-align: center;
  }
`;

const CTATitle = styled.h2`
  font-size: 42px;
  font-weight: bold;
  margin: 0;
  color: ${props => props.theme.colors.primary};
  line-height: 1.2;
  flex: 1;
  min-width: 250px;
  text-align: center;
  
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 32px;
    min-width: unset;
  }
`;

const CTAText = styled.div`
  flex: 1;
  font-size: 18px;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 24px;
`;

const SparkleContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
  top: 0;
  left: 0;
`;

const Sparkle = styled.span`
  position: absolute;
  font-size: 10px;
  color: ${props => props.theme.colors.accent1};
  opacity: 0;
  animation: ${sparkle} 2s ease-in-out infinite;
  
  &:nth-child(1) {
    top: 20%;
    left: 15%;
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    top: 60%;
    right: 20%;
    animation-delay: 1s;
  }
  
  &:nth-child(3) {
    bottom: 25%;
    left: 25%;
    animation-delay: 0.5s;
  }
`;

const CTAButton = styled.button`
  padding: 16px 40px;
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 50%, ${props => props.theme.colors.secondary} 100%);
  background-size: 200% 200%;
  color: white;
  border: 2px solid ${props => props.theme.colors.accent1};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible;
  margin-top: 50px;
  display: block;
  margin-left: auto;
  margin-right: auto;
  box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4), 0 0 20px rgba(241, 222, 124, 0.3);
  animation: ${glow} 3s ease-in-out infinite;

  &::before {
    content: '✨';
    position: absolute;
    font-size: 14px;
    top: -8px;
    left: 10px;
    animation: ${sparkle} 2s ease-in-out infinite;
    pointer-events: none;
  }

  &::after {
    content: '⭐';
    position: absolute;
    font-size: 12px;
    bottom: -6px;
    right: 15px;
    animation: ${sparkle} 2.5s ease-in-out infinite 0.5s;
    pointer-events: none;
  }
  
  &:hover {
    background-position: 100% 0;
    box-shadow: 0 8px 35px rgba(219, 39, 98, 0.5), 0 0 24px rgba(241, 222, 124, 0.4);
    transform: scale(1.01);
    animation: none;
  }

  &:active {
    transform: scale(0.98);
    box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4);
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
  }
`;

const SectionHeaderStarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    gap: 12px;
  }
`;

const AboutMe = ({ 
  heroImage = Images.owner2, 
  heroImageAlt = "Michelle Mey Köhler - Englischlehrerin",
  showImage = true 
}) => {
  const [isPopupOpen, setIsPopupOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth <= mobileBreakpoint);
  const { isAuthenticated } = useAuth();
  const authed = isAuthenticated();

  React.useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= mobileBreakpoint);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  React.useEffect(() => {
    const viewportWidth = window.innerWidth;
    fetch(config.getApiUrl(`/api/common/about-me?viewportWidth=${viewportWidth}`), { method: 'GET' })
      .catch(() => {});
  }, []);

  // Preload owner images for faster loading
  React.useEffect(() => {
    const imgAvif = new Image();
    imgAvif.src = Images.ownerAvif;
    const imgWebp = new Image();
    imgWebp.src = Images.ownerWebp;
    // Preload owner2 images
    const img2Avif = new Image();
    img2Avif.src = Images.owner2Avif;
    const img2Webp = new Image();
    img2Webp.src = Images.owner2Webp;
    // Also preload heroImage if it's different from owner/owner2
    if (heroImage !== Images.owner && heroImage !== Images.ownerAvif && heroImage !== Images.owner2) {
      const img = new Image();
      img.src = heroImage;
    }
  }, [heroImage]);

  const handleOpenPopup = () => {
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  // Split personal story into two paragraphs based on the design
  // First paragraph: about how learning English changed her life
  // Second paragraph: about becoming a teacher
  const personalStoryText = texts.personalStory;
  const firstParagraph = 'Hallo! Ich bin <strong>Michelle</strong>, aber alle nennen mich <strong><span style="color: #db2762">Mey</span></strong>. Meine Reise mit der englischen Sprache begann schon in meiner Kindheit, als ich fasziniert von der Möglichkeit war, mit Menschen aus der ganzen Welt zu kommunizieren. Diese Faszination hat mir Türen zu neuen Möglichkeiten, Wissen und internationalen Freundschaften geöffnet.';
  const secondParagraph = 'Diese Faszination hat mich dazu gebracht, Englischlehrerin zu werden und meine Leidenschaft für Sprachen an andere weiterzugeben. Ich möchte meinen Schülern helfen, ihre Lernziele zu erreichen und neue Welten für sie zu öffnen.';

  const qualificationsSubtitle = 'Türen, die mir die englische Sprache eröffnet hat und mich zur Englischlehrerin qualifizieren:';

  return (
    <PageLayout onTerminClick={handleOpenPopup}>
      <ContentContainer>
        {/* Über Mich Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <StarBadgeWithIcon />
            <MainTitle>{texts.aboutMeTitle}</MainTitle>
          </HeroTitle>
          <Subtitle>{texts.aboutMeSubtitle}</Subtitle>
          <Description>
              <p dangerouslySetInnerHTML={{ __html: firstParagraph }} />
              <p dangerouslySetInnerHTML={{ __html: secondParagraph }} />
          </Description>
        </HeroContent>
        
        {showImage && (
          <ImageWithBlobWrapper>
            <BackgroundShape />
            <OwnerImageContainer>
              {heroImage === Images.owner || heroImage === Images.ownerAvif ? (
                <OwnerPicture>
                  <source srcSet={Images.ownerAvif} type="image/avif" />
                  <OwnerImage 
                    src={Images.ownerWebp} 
                    alt={heroImageAlt}
                    width="400"
                    height="400"
                    loading="eager"
                    fetchPriority="high"
                  />
                </OwnerPicture>
              ) : heroImage === Images.owner2 ? (
                <OwnerPicture>
                  <source srcSet={Images.owner2Avif} type="image/avif" />
                  <OwnerImage 
                    src={Images.owner2Webp} 
                    alt={heroImageAlt}
                    width="400"
                    height="400"
                    loading="eager"
                    fetchPriority="high"
                  />
                </OwnerPicture>
              ) : (
                <OwnerImage 
                  src={heroImage} 
                  alt={heroImageAlt}
                  width="400"
                  height="400"
                  loading="eager"
                  fetchPriority="high"
                />
              )}
            </OwnerImageContainer>
          </ImageWithBlobWrapper>
        )}
      </HeroSection>

        {/* Qualifikationen & Zertifizierungen Section */}
      <ContentSection>
          <SectionHeader>
            <SectionHeaderStar style={{ opacity: 0 }} src={Images.star1} alt="Star icon" />
            <div>
              <SectionHeaderStarContainer>
                <SectionHeaderStarSecond src={Images.star1} alt="Star icon" />
                <SectionTitle titleColor={theme.colors.secondary}>{texts.qualificationsTitle}</SectionTitle>
              </SectionHeaderStarContainer>
            
          <SectionText dangerouslySetInnerHTML={{ __html: texts.qualifications }} />
            </div>
            
          </SectionHeader>
      </ContentSection>

        {/* Meine Unterrichtsphilosophie Section */}
      <ContentSection>
          <SectionHeader>
            <SectionHeaderStar style={{ opacity: 0 }} src={Images.star2} alt="Star icon" />
            <div>
              <SectionHeaderStarContainer>
                <SectionHeaderStarSecond src={Images.star2} alt="Star icon" />
                <SectionTitle titleColor={theme.colors.secondary}>{texts.teachingPhilosophyTitle}</SectionTitle>
              </SectionHeaderStarContainer>
        <SectionText dangerouslySetInnerHTML={{ __html: texts.experience }} />
            </div>
          </SectionHeader>
      </ContentSection>

        {/* Call to Action Section */}
        {!authed && (
          <CTASection>
            <CTAContent>
              <CTATitle>Bereit für deine Englisch-Reise?</CTATitle>
              <div>
                <CTAText>
                  Ob bessere Schulnoten, Verständigen im Urlaub oder komfortabel am Arbeitsplatz unterhalten, neue Freunde finden oder Auswandern - gemeinsam finden wir den perfekten Weg für dich und deine Familie.
                </CTAText>
            <CTAButton onClick={handleOpenPopup}>
              <SparkleContainer>
                <Sparkle>✨</Sparkle>
                <Sparkle>⭐</Sparkle>
                <Sparkle>✨</Sparkle>
              </SparkleContainer>
              Kostenlose Probestunde
            </CTAButton>
              </div>
            </CTAContent>
          </CTASection>
        )}
      </ContentContainer>
      
      <Popup 
        isOpen={isPopupOpen} 
        onClose={handleClosePopup}
        title="Probestunde"
      />
    </PageLayout>
  );
};

export default AboutMe; 
